import { QueryObserver } from "@tanstack/query-core";
import projectSelector from "@rflocal/shared/utils/projectSelector";
import globalStore from "../../../../../state";
import type { WorkspaceState } from "../../../../../state/slices";
import { getPendingJoinRequests } from "../../../../../backendapi/workspaceApi";
import { queryClient } from "../../../../queryClient";
import { ActivityUIKind, activityQueryKeys } from "../constants";
import type { ActivitySource, ActivityViewData, JoinRequestMeta } from "../types";
import { toDate } from "./utils";
import type { FirestoreTimestampLike } from "./types";

interface JoinRequestDoc {
    id: string;
    workspaceId: string;
    fromEmail?: string;
    requestedAt?: FirestoreTimestampLike;
}

/**
 * Follows the backend's `activityService.buildEventId` convention
 * (`{workspaceId}:{kind}:{entityId}`) so join request IDs are consistent
 * with real `activity_events` IDs and work with the shared read-state
 * store (`activity_event_interactions`).
 */
function buildJoinRequestId(workspaceId: string, requestDocId: string): string {
    return `${workspaceId}:join_request:${requestDocId}`;
}

const REFETCH_INTERVAL_MS = 60_000;

/**
 * Per-workspace fetcher — goes through `queryClient.fetchQuery` so each workspace
 * gets its own cache entry keyed by `joinRequestsByWorkspace(workspaceId)`.
 *
 * Benefits over the previous raw `Promise.all` fan-out:
 * - successful fetches survive polling intervals instead of being discarded
 *   and re-fetched every 60s
 * - failed workspaces retry independently per react-query's retry policy
 *   (they don't drag the whole outer query into an error state)
 * - future `acceptJoinRequest` / `denyJoinRequest` mutations can invalidate
 *   just the affected workspace's key instead of the whole user's source
 */
async function fetchWorkspaceJoinRequests(workspaceId: string): Promise<JoinRequestDoc[]> {
    return queryClient.fetchQuery({
        queryKey: activityQueryKeys.joinRequestsByWorkspace(workspaceId),
        queryFn: () => getPendingJoinRequests(workspaceId),
        // Matches the outer polling cadence so within a single poll cycle we serve
        // from cache; the next cycle's fetchQuery call re-fetches past staleness.
        staleTime: REFETCH_INTERVAL_MS
    });
}

/**
 * QueryFn — fetches pending join requests across all joinable workspaces.
 *
 * Per-workspace error isolation: each inner fetchQuery resolves to a tagged
 * result so a single failing workspace doesn't prevent others from appearing.
 * If *all* fetches fail, the function throws so the outer QueryObserver's
 * built-in retry kicks in.
 */
async function fetchJoinRequests(userId: string): Promise<ActivityViewData[]> {
    const isSupportUser = projectSelector.supportUser?.()?.id === userId;
    const isSSO = projectSelector.isSSODomain?.();
    if (isSupportUser || isSSO) return [];

    // Read at query time — if workspaces haven't loaded yet the first poll
    // returns [] and the 60s refetchInterval picks them up. Same timing as
    // the old NavbarController.fetchInvites. Making this reactive would require
    // subscribing to the workspacesById slice, which isn't wired today.
    const workspaces =
        (globalStore.getState() as Pick<WorkspaceState, "workspacesById">).workspacesById ?? {};
    const joinableIds = Object.keys(workspaces).filter(
        (id) => (workspaces[id] as { joinable?: boolean } | undefined)?.joinable
    );
    if (joinableIds.length === 0) return [];

    const results = await Promise.all(
        joinableIds.map((id) =>
            fetchWorkspaceJoinRequests(id)
                .then((data) => ({ data, ok: true as const }))
                .catch(() => ({ data: [] as JoinRequestDoc[], ok: false as const }))
        )
    );

    if (!results.some((r) => r.ok)) {
        throw new Error("All join request fetches failed");
    }

    const activities: ActivityViewData[] = [];

    for (const request of results.flatMap((r) => r.data).filter(Boolean) as JoinRequestDoc[]) {
        const meta: JoinRequestMeta = { workspaceId: request.workspaceId, requestId: request.id };
        activities.push({
            id: buildJoinRequestId(request.workspaceId, request.id),
            kind: ActivityUIKind.JoinRequest,
            title: request.fromEmail || "Join request",
            description: "Wants to join the workspace",
            icon: "far fa-user-plus",
            createdAt: toDate(request.requestedAt),
            meta
        });
    }

    return activities;
}

/**
 * ActivitySource for workspace join requests.
 *
 * Fetches pending join requests across all of the user's joinable workspaces.
 * Gated by SSO and support-user checks — returns nothing in those contexts.
 * Backed by a QueryObserver for polling, caching, error retry, and
 * cache invalidation.
 */
class JoinRequestSource implements ActivitySource {
    private userId: string;

    constructor(userId: string) {
        this.userId = userId;
    }

    subscribe(callback: (activities: ActivityViewData[]) => void): () => void {
        const { userId } = this;
        const observer = new QueryObserver(queryClient, {
            queryKey: activityQueryKeys.joinRequests(userId),
            queryFn: () => fetchJoinRequests(userId),
            refetchInterval: REFETCH_INTERVAL_MS
        });

        const unsubscribe = observer.subscribe((result) => {
            if (result.data) {
                callback(result.data);
            } else if (result.error) {
                callback([]);
            }
        });

        return () => {
            unsubscribe();
            observer.destroy();
        };
    }
}

export { JoinRequestSource };
